import { ContentItem } from '@deltek/specification-client/core/Models/contentItem'
import { ProductFamily } from '@deltek/specification-client/core/Models/productFamily'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import {
    SetProjectContentItemIsHiddenInput,
    ViewItemReorderInput,
} from '@deltek/specification-client/core/Models/projectProductFamily'
import { AddProjectContentItemInput } from '@deltek/specification-client/core/Models/projectProductProperty'
import { ViewItemReorderPTPInput } from '@deltek/specification-client/core/Models/projectProductTypeProperty'
import { ExtendedContentInsertVariable } from 'api/graphQL/types/contentInsert.types'
import { NuggetResponse } from 'api/graphQL/types/projectElement.types'
import { ProductFamilyStudioManagement } from 'models/StudioManagement/studioManagement'
import { AddProductTypeInput } from 'models/addProductTypeInput'
import { UnitsOfMeasure } from 'models/projectSettings'
import React from 'react'
import type { TextVariant } from '../constants'
import { NormalizedDocumentTemplateViewItem } from './model'

export const AreaContext = React.createContext({} as AreaProviderProps)

export interface AreaProviderProps {
    type: 'Family' | 'ProductType' | 'Sustainability Topic' | 'All'
    selectedTopic?: Partial<ProductFamilyStudioManagement>
    productFamily?: Partial<ProductFamily>
    nugget?: NuggetResponse
    showPrefix?: boolean
    tab?: number
    setTab?: (value: number) => void
    disableArticleReorder?: boolean
    areNotesVisible?: boolean
    areHiddenPropertiesVisible?: boolean
    itemsExpanded?: boolean
    selectedProjectNode?: ProjectElementNode | null
    selectedElementId?: string
    projectId?: string
    handleSingleArticleExpandClick?: (articleId: string) => void
    handleExpandClick?: (value: boolean) => void
    handleFamilyReorderTemplates?: (
        reOrderFamilyContentInput: Partial<ViewItemReorderInput>
    ) => void
    handleProductTypeReorderTemplates?: (
        reOrderPTContentInput: Partial<ViewItemReorderPTPInput>
    ) => void
    handleInActivateProperty?: (
        SetProjectContentItemIsHiddenInput: Partial<
            SetProjectContentItemIsHiddenInput
        >
    ) => void
    handleBestPracticeProperty?: (item: Partial<ContentItem>) => void
    persistState?: (propKey: string, inputValue: boolean | any) => void
    getPersistedState?: (propKey: string) => any
    unitsOfMeasure: UnitsOfMeasure
    updateExpandState?: (articleId: string) => void
    expandStateList?: string[]
    setManufacturerArticleId?: (articleIds: string[]) => void
    manufacturerArticleIds?: string[]
    isContentItemReOrdered?: boolean
    handleIsContentItemReOrdered?: (state: boolean) => void
    handleAddNewProperty: (
        inputValues: Partial<AddProjectContentItemInput>,
        rootArticleContentItemIds?: string[],
        siblingIds?: string[],
        isProductGroup?: boolean,
        indexOfPillForNewPt?: number | undefined,
        typeOfPillForNewPt?: TextVariant,
        isPillWithNewPara?: boolean,
        productTypeNodeInFamily?: Partial<ProjectElementNode>
    ) => void
    handleEditKeyChanges: (
        textType: string,
        propertyKey?: string,
        siblings?: string[]
    ) => boolean
    handleChangeParagraphItemId?: (value: string) => void
    selectedParagraphItemId?: string
    handleChangeParagraphActionState?: (value: string) => void
    selectedParagraphActionState?: string
    selectedSpecAgentIdPt?: string
    retrieveUpdatedElementChanges: () => void
    isMainWrapperScrolling?: boolean
    handleChangeMainWrapperScrolling?: (value: boolean) => void

    /** Function to call when the user tries to paste HTML paragraphs (e.g., copied from Word).
     * @param html - The HTML content from the clipboard
     * @param index - The 0-based index where the insert is occurring. For instance, if the
     * first pasted paragraph should appear after the second child of the parent, index would be 2.
     * @param contentItemParentId - The ID of the parent where the user is
     * pasting content. Should be null if at root level (e.g., when pasting articles)
     * @param contentItemParentType - The type of object whose ID is contentItemParentId. Should be
     * "None" if content will begin at root level, "Nugget" if parent is a Nugget (e.g., product type or general project nugget),
     * "ProductTypeProperty" if parent is a PTP, or "ContentItem" if parent is a nested additional content of either a Nugget or a PTP.
     * @param contentItemOwnerId - If adding content to an existing Nugget/PTP, the ID of that object. If creating a whole new Nugget/PTP, null.
     * @param contentItemOwnerType - Type of object whose ID is given by ContentItemOwnerId. Ignored if ContentItemOwnerId is null.
     *  Possible values: "Nugget", "ProductTypeProperty"
     * @param productFamilyId - The ID of the project product family that's open in the workspace.
     * @param productTypePropertyType - "Shared" if any new properties created would be shared (family-level) properties,
     * "Specific" if they would be PT-specific properties
     * @param startingLevel - This list level (1-6) where the first item will be pasted.
     * @param partType - The part type of content item. Possible values: 'GENERAL','PRODUCTS','EXECUTION','ContentGroup'
     * @param rootArticleContentItemIds - Array containing ID(s) of root article content item(s) that we want to reload after paste processes.
     * @param parentPropertyKey - Fully-qualified ("|"-delimited) key of parent property, if any.
     */
    handleHtmlParagraphPaste?: (
        contentInsertVariables: ExtendedContentInsertVariable
    ) => void
    /**
     * Callback for updating the element list with a new (e.g., duplicate) product type
     *
     * @param element the new product type element to insert into the project element tree
     */
    insertNewProductTypeElement?: (
        element: ProjectElementNode,
        isSelectedFromListingDialog?: boolean,
        newNodesForAdditionalInstances?: ProjectElementNode[],
        isNewPT?: boolean
    ) => void
    addNewProductTypeIntoTree?: (newNode?: AddProductTypeInput) => void
    handleAddSustainabilityTopic?: (
        template: NormalizedDocumentTemplateViewItem,
        parent: NormalizedDocumentTemplateViewItem,
        topicIds: string[]
    ) => void
}

export const AreaProvider: React.FC<AreaProviderProps> = ({
    type,
    showPrefix = true,
    productFamily,
    nugget,
    setTab,
    disableArticleReorder,
    itemsExpanded,
    handleExpandClick,
    handleFamilyReorderTemplates,
    handleProductTypeReorderTemplates,
    children,
    areNotesVisible,
    handleInActivateProperty,
    handleBestPracticeProperty,
    areHiddenPropertiesVisible,
    selectedProjectNode,
    selectedElementId,
    persistState,
    getPersistedState,
    handleSingleArticleExpandClick,
    projectId,
    unitsOfMeasure,
    updateExpandState,
    expandStateList,
    setManufacturerArticleId,
    manufacturerArticleIds,
    handleAddNewProperty,
    handleEditKeyChanges,
    retrieveUpdatedElementChanges,
    selectedSpecAgentIdPt,
    selectedTopic,
    handleHtmlParagraphPaste,
    insertNewProductTypeElement,
    addNewProductTypeIntoTree,
    handleAddSustainabilityTopic,
}) => {
    const [isContentItemReOrdered, setIsContentItemReOrdered] = React.useState<
        boolean
    >(false)

    const handleIsContentItemReOrdered = React.useCallback(
        (state: boolean) => {
            setIsContentItemReOrdered(state)
        },
        [setIsContentItemReOrdered]
    )
    const [
        selectedParagraphItemId,
        setSelectedParagraphItemId,
    ] = React.useState<string>('')
    const [
        selectedParagraphActionState,
        setSelectedParagraphActionState,
    ] = React.useState<string>('')
    const handleChangeParagraphItemId = React.useCallback(
        (value: string) => {
            setSelectedParagraphItemId(value)
        },
        [setSelectedParagraphItemId]
    )
    const handleChangeParagraphActionState = React.useCallback(
        (value: string) => {
            setSelectedParagraphActionState(value)
        },
        [setSelectedParagraphActionState]
    )
    return (
        <AreaContext.Provider
            value={{
                type,
                showPrefix,
                productFamily,
                nugget,
                setTab,
                disableArticleReorder,
                areNotesVisible,
                areHiddenPropertiesVisible,
                itemsExpanded,
                handleExpandClick,
                handleFamilyReorderTemplates,
                handleProductTypeReorderTemplates,
                handleInActivateProperty,
                handleBestPracticeProperty,
                selectedProjectNode,
                selectedElementId,
                persistState,
                getPersistedState,
                handleSingleArticleExpandClick,
                projectId,
                unitsOfMeasure,
                updateExpandState,
                expandStateList,
                setManufacturerArticleId,
                manufacturerArticleIds,
                isContentItemReOrdered,
                handleIsContentItemReOrdered,
                handleAddNewProperty,
                handleEditKeyChanges,
                handleChangeParagraphItemId,
                selectedParagraphItemId,
                handleChangeParagraphActionState,
                selectedParagraphActionState,
                selectedSpecAgentIdPt,
                retrieveUpdatedElementChanges,
                selectedTopic,
                handleHtmlParagraphPaste,
                insertNewProductTypeElement,
                addNewProductTypeIntoTree,
                handleAddSustainabilityTopic,
            }}
        >
            {children}
        </AreaContext.Provider>
    )
}
